import React, { useCallback, useEffect, useState } from "react";
import { Container } from "reactstrap";
import { useParams } from "react-router-dom";
import PageLoadingSpinner from "components/common/PageLoader";
import BreadCrumb from "components/common/BreadCrumb";
import { getDocumentTitle } from "utils/helpers";
import { getEnquiry } from "../services/EnquiryService";
import type { Enquiry } from "../types/enquiry.types";
import EnquiryOverviewContext from "./EnquiryOverviewContext";
import Section from "./Section";

const EnquiryOverview = () => {
  document.title = getDocumentTitle("Enquiry Details");
  const { id } = useParams();
  const [enquiry, setEnquiry] = useState<Enquiry | null>(null);
  const [loading, setLoading] = useState(true);
  const refetch = useCallback(async () => {
    if (!id) return;
    const numericId = Number(id);
    try {
      const [enquiryRes] = await Promise.all([getEnquiry(numericId)]);

      if (enquiryRes.success) {
        setEnquiry(enquiryRes.data);
      } else {
        setEnquiry(null);
      }
    } catch (err) {
      console.warn(err);
      setEnquiry(null);
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      await refetch();
      if (!cancelled) setLoading(false);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [id, refetch]);

  if (loading) return <PageLoadingSpinner message="Loading enquiry..." />;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Enquiry Details"
          pageTitle="Enquiry Management"
          items={[
            { label: "Enquiries", path: "/enquiry-management/enquiries" },
          ]}
        />
        <EnquiryOverviewContext.Provider value={{ enquiry, refetch }}>
          <Section />
        </EnquiryOverviewContext.Provider>
      </Container>
    </div>
  );
};

export default EnquiryOverview;
